import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Container from '../components/common/Container';
import { Heading, Subheading } from '../components/common/TextComponents';
import Button from '../components/common/Button';
import { Logo } from '../components/common/Logo';
import { useAuthContext } from '../hooks/useAuthContext';
import { useLogout } from '../hooks/useLogout';
import { collection, getDocs, getFirestore, query, where, doc, getDoc, updateDoc, addDoc, setDoc, serverTimestamp, limit } from 'firebase/firestore';
import { getProfileSummary, saveProfileSummary } from '../services/firebase';
import { toast } from 'react-hot-toast';
import AdminLayout from '../components/admin/AdminLayout';
import PageLayout from '../components/layout/PageLayout';

// List of admin user emails - in production, this should be stored in Firestore
const ADMIN_EMAILS = ['grey@justburnt.com', 'arjan@justburnt.com', 'louie@justburnt.com'];

const AdminDashboard = () => {
  const { user } = useAuthContext();
  const { logout } = useLogout();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [profileSummary, setProfileSummary] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');
  const [rawInputValues, setRawInputValues] = useState({});
  
  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    
    if (!ADMIN_EMAILS.includes(user.email)) {
      navigate('/dashboard');
      return;
    }
    
    // Fetch all users
    const fetchUsers = async () => {
      setLoading(true);
      try {
        console.log("Fetching users as admin:", user.email);
        const db = getFirestore();
        
        // First try to fetch users from user_profiles collection
        console.log("Fetching from user_profiles collection...");
        const userProfilesSnapshot = await getDocs(collection(db, 'user_profiles'));
        console.log("User profiles found:", userProfilesSnapshot.docs.length);
        
        const userIds = [];
        const usersData = [];
        
        // Process user profiles data
        userProfilesSnapshot.docs.forEach(doc => {
          const data = doc.data();
          console.log("User profile:", doc.id, data);
          
          // Add user profile data
          // Try to get a proper display name (including old structures)
          let displayName = 'Name not available';
          if (data.basicInfo?.firstName && data.basicInfo?.lastName) {
            displayName = `${data.basicInfo.firstName} ${data.basicInfo.lastName}`;
          } else if (data.firstName && data.lastName) {
            displayName = `${data.firstName} ${data.lastName}`;
          } else if (data.basicInfo?.name) {
            displayName = data.basicInfo.name;
          } else if (data.name) {
            displayName = data.name;
          } else if (data.displayName) {
            displayName = data.displayName;
          } else if (data.basicInfo?.email) {
            const emailName = data.basicInfo.email.split('@')[0];
            displayName = emailName.charAt(0).toUpperCase() + emailName.slice(1);
          } else if (data.email) {
            const emailName = data.email.split('@')[0];
            displayName = emailName.charAt(0).toUpperCase() + emailName.slice(1);
          }
          
          const userData = {
            id: doc.id,
            email: data.basicInfo?.email || data.email || 'Unknown Email',
            displayName: displayName,
            isAdmin: data.isAdmin || false,
            lastLogin: data.lastLogin ? new Date(data.lastLogin.seconds * 1000).toLocaleString() : 'Never',
            createdAt: data.createdAt ? new Date(data.createdAt.seconds * 1000).toLocaleString() : 'Unknown',
            hasUserProfile: true,
            waitlistSubmissionId: data.waitlistSubmissionId || null,
            hasWaitlistData: !!data.waitlistSubmissionId
          };
          
          userIds.push(doc.id);
          usersData.push(userData);
        });
        
        // Also fetch users from user_profile_summaries to catch new users with empty profiles
        console.log("Fetching from user_profile_summaries collection...");
        const profileSummariesSnapshot = await getDocs(collection(db, 'user_profile_summaries'));
        console.log("Profile summaries found:", profileSummariesSnapshot.docs.length);
        
        profileSummariesSnapshot.docs.forEach(doc => {
          const data = doc.data();
          // Only add if not already in our list
          if (!userIds.includes(doc.id)) {
            console.log("Found user with profile summary but no user profile:", doc.id, data);
            
            // Look for an existing user profile with this ID to get the email
            const matchingUser = userProfilesSnapshot.docs.find(profileDoc => profileDoc.id === doc.id);
            const email = matchingUser ? 
              (matchingUser.data().email || matchingUser.data().basicInfo?.email) : 
              (data.userID || doc.id);
            
            // Try to get a better name than just "Name not available"
            let displayName = "Name not available";
            if (email && email !== doc.id) {
              // Create a display name from the email (first part)
              const emailName = email.split('@')[0];
              displayName = emailName.charAt(0).toUpperCase() + emailName.slice(1);
            }
            
            userIds.push(doc.id);
            usersData.push({
              id: doc.id,
              email: email,
              displayName: displayName,
              hasProfileSummary: true,
              hasUserProfile: false,
              hasWaitlistData: false
            });
          }
        });
        
        // Now get all users from waitlist collection to make sure we don't miss any
        console.log("Also fetching from waitlist collection...");
        const waitlistSnapshot = await getDocs(collection(db, 'waitlist'));
        console.log("Waitlist documents:", waitlistSnapshot.docs.length);
        
        waitlistSnapshot.docs.forEach(doc => {
          const data = doc.data();
          
          // Only add users that aren't already in our list
          if (data.userId && !userIds.includes(data.userId)) {
            userIds.push(data.userId);
            
            // Try to create a proper display name (check multiple structures)
            let displayName = 'Name not available';
            if (data.userInfo?.firstName && data.userInfo?.lastName) {
              displayName = `${data.userInfo.firstName} ${data.userInfo.lastName}`;
            } else if (data.firstName && data.lastName) {
              displayName = `${data.firstName} ${data.lastName}`;
            } else if (data.userInfo?.name) {
              displayName = data.userInfo.name;
            } else if (data.name) {
              displayName = data.name;
            } else if (data.userInfo?.email) {
              const emailName = data.userInfo.email.split('@')[0];
              displayName = emailName.charAt(0).toUpperCase() + emailName.slice(1);
            } else if (data.email) {
              const emailName = data.email.split('@')[0];
              displayName = emailName.charAt(0).toUpperCase() + emailName.slice(1);
            }
            
            const userData = {
              id: data.userId,
              email: data.userInfo?.email || 'Unknown Email',
              displayName: displayName,
              submissionId: doc.id,
              submissionDate: data.submittedAt || data.createdAt,
              hasWaitlistData: true,
              hasUserProfile: false
            };
            
            usersData.push(userData);
          } else if (!data.userId) {
            // Add orphaned submissions with no user ID
            // Try to create a proper display name (check multiple structures)
            let displayName = 'Name not available (Orphaned)';
            if (data.userInfo?.firstName && data.userInfo?.lastName) {
              displayName = `${data.userInfo.firstName} ${data.userInfo.lastName} (Orphaned)`;
            } else if (data.firstName && data.lastName) {
              displayName = `${data.firstName} ${data.lastName} (Orphaned)`;
            } else if (data.userInfo?.name) {
              displayName = `${data.userInfo.name} (Orphaned)`;
            } else if (data.name) {
              displayName = `${data.name} (Orphaned)`;
            } else if (data.userInfo?.email) {
              const emailName = data.userInfo.email.split('@')[0];
              displayName = `${emailName.charAt(0).toUpperCase() + emailName.slice(1)} (Orphaned)`;
            } else if (data.email) {
              const emailName = data.email.split('@')[0];
              displayName = `${emailName.charAt(0).toUpperCase() + emailName.slice(1)} (Orphaned)`;
            }
            
            const userData = {
              id: doc.id, // Use the doc ID since there's no user ID
              email: data.userInfo?.email || 'Unknown Email',
              displayName: displayName,
              submissionId: doc.id,
              submissionDate: data.submittedAt || data.createdAt,
              isOrphaned: true,
              hasWaitlistData: true,
              hasUserProfile: false
            };
            
            // Don't add to userIds because this isn't a real user ID
            usersData.push(userData);
          }
        });
        
        // Get user_submissions_map for additional user IDs
        console.log("Fetching from user_submissions_map collection...");
        const mapSnapshot = await getDocs(collection(db, 'user_submissions_map'));
        console.log("User submissions map documents:", mapSnapshot.docs.length);
        
        mapSnapshot.docs.forEach(doc => {
          console.log("Map doc:", doc.id, doc.data());
          // If this user ID isn't already in our list, add it
          if (!userIds.includes(doc.id)) {
            userIds.push(doc.id);
            usersData.push({
              id: doc.id,
              submissionId: doc.data().submissionId,
              // Other fields will be marked as unknown
              displayName: 'User ' + doc.id.substring(0, 6),
              email: 'Unknown',
              hasMapData: true,
              hasUserProfile: false
            });
          }
        });
        
        console.log("Total users found:", usersData.length);
        setUsers(usersData);
      } catch (err) {
        console.error("Error fetching users:", err);
        
        // If we get a permissions error, try a different approach for admin
        if (err.message.includes("Missing or insufficient permissions")) {
          console.log("Trying alternative approach for admin access...");
          try {
            // Create at least one dummy user to show the admin interface works
            setUsers([
              {
                id: "admin-test",
                displayName: "Sample User (Permissions Issue)",
                email: "example@justburnt.com",
                submissionId: null
              }
            ]);
            setError(`${err.message}. Firebase security rules have been updated and deployed. Please refresh the page to try again.`);
          } catch (fallbackErr) {
            console.error("Fallback approach also failed:", fallbackErr);
            setError(`${err.message}. Please check your Firestore security rules.`);
          }
        } else {
          setError(`Error fetching users: ${err.message}`);
        }
      } finally {
        setLoading(false);
      }
    };
    
    fetchUsers();
  }, [user, navigate]);
  
  const handleUserSelect = async (userId) => {
    setSelectedUser(userId);
    
    try {
      const db = getFirestore();
      
      // First check if there's a user profile in user_profiles collection
      console.log("Checking for user profile in user_profiles collection");
      const userProfileRef = doc(db, 'user_profiles', userId);
      const userProfileSnap = await getDoc(userProfileRef);
      
      let userProfileData = null;
      let profileSummaryData = null;
      
      if (userProfileSnap.exists()) {
        console.log("Found user profile:", userProfileSnap.data());
        userProfileData = userProfileSnap.data();
      }
      
      // Always check for profile summary data, even if no user profile exists
      console.log("Checking for profile summary data");
      const profileSummaryRef = doc(db, 'user_profile_summaries', userId);
      const profileSummarySnap = await getDoc(profileSummaryRef);
      
      if (profileSummarySnap.exists()) {
        console.log("Found profile summary:", profileSummarySnap.data());
        profileSummaryData = profileSummarySnap.data();
      }
      
      // Check waitlist collection as a fallback or to get additional data
      console.log("Checking waitlist collection for additional data");
      
      let waitlistData = null;
      let submissionId = null;
      
      // First, check if we have a waitlist submission ID in the user profile
      if (userProfileData && userProfileData.waitlistSubmissionId) {
        submissionId = userProfileData.waitlistSubmissionId;
        const waitlistRef = doc(db, 'waitlist', submissionId);
        const waitlistSnap = await getDoc(waitlistRef);
        
        if (waitlistSnap.exists()) {
          waitlistData = waitlistSnap.data();
          console.log("Found waitlist data from user profile reference:", waitlistData);
        }
      } 
      // If no waitlist submission ID in user profile, try to find by user ID
      else {
        const waitlistRef = collection(db, 'waitlist');
        const q = query(waitlistRef, where('userId', '==', userId));
        const querySnapshot = await getDocs(q);
        
        if (!querySnapshot.empty) {
          waitlistData = querySnapshot.docs[0].data();
          submissionId = querySnapshot.docs[0].id;
          console.log("Found waitlist data by user ID query:", waitlistData);
        } 
        // If this is an "orphaned" submission and the userId is actually a submission ID
        else if (userId.length > 10 && !userId.includes('auth')) {
          // Try to get it directly
          const orphanedRef = doc(db, 'waitlist', userId);
          const orphanedSnap = await getDoc(orphanedRef);
          
          if (orphanedSnap.exists()) {
            waitlistData = orphanedSnap.data();
            submissionId = userId;
            console.log("Found waitlist data as orphaned entry:", waitlistData);
          }
        }
      }
      
      // Set user profile data for the UI
      if (userProfileData) {
        console.log("Setting user profile data:", userProfileData);
        setUserProfile(userProfileData);
      } else {
        setUserProfile(null);
      }
      
      // Assemble the combined profile data from all sources
      const combinedProfileData = {
        // Basic user info from user profile or waitlist
        id: userId,
        name: userProfileData?.basicInfo?.name || userProfileData?.displayName || waitlistData?.userInfo?.name || 'Unknown',
        email: userProfileData?.basicInfo?.email || userProfileData?.email || waitlistData?.userInfo?.email || profileSummaryData?.userID || 'Unknown',
        submissionId: submissionId,
        
        // Profile summary data from profile_summaries collection or waitlist
        profileSummary: profileSummaryData?.profileSummary || waitlistData?.profileSummary?.profileSummary || '',
        keyHighlights: profileSummaryData?.keyHighlights || waitlistData?.profileSummary?.keyHighlights || {},
        idealRoleCharacteristics: profileSummaryData?.idealRoleCharacteristics || waitlistData?.profileSummary?.idealRoleCharacteristics || {},
        keySkills: profileSummaryData?.keySkills || [],
        painPoints: profileSummaryData?.painPoints || [],
        potentialFit: profileSummaryData?.potentialFit || '',
        
        // Admin notes from waitlist
        notes: waitlistData?.adminNotes?.notes || '',
        status: waitlistData?.adminNotes?.status || 'pending',
        rating: waitlistData?.adminNotes?.rating || '3',
        tags: waitlistData?.adminNotes?.tags || [],
        
        // Additional metadata
        createdAt: userProfileData?.createdAt ? new Date(userProfileData.createdAt.seconds * 1000).toLocaleString() : 
                  (profileSummaryData?.createdAt ? new Date(profileSummaryData.createdAt.seconds * 1000).toLocaleString() : 'Unknown'),
        lastLogin: userProfileData?.lastLogin ? new Date(userProfileData.lastLogin.seconds * 1000).toLocaleString() : 'Never',
        isAdmin: userProfileData?.isAdmin || false,
        
        // Preferences from user profile
        preferences: userProfileData?.preferences || {},
        
        // Resume info from user profile
        resumeUrl: userProfileData?.resumeUrl || null,
        resumeFilename: userProfileData?.resumeFilename || null,
        
        // Whether user has specific data available
        hasUserProfile: !!userProfileData,
        hasProfileSummary: !!profileSummaryData,
        hasWaitlistData: !!waitlistData
      };
      
      console.log("Setting combined profile data:", combinedProfileData);
      setProfileSummary(combinedProfileData);
    } catch (error) {
      console.error("Error fetching user data:", error);
      setError(`Error fetching data for user ${userId}: ${error.message}`);
    }
  };
  
  const handleSaveProfileSummary = async () => {
    try {
      setIsSaving(true);
      
      if (!selectedUser) {
        toast.error("No user selected to save profile summary for");
        setIsSaving(false);
        return;
      }
      
      const db = getFirestore();
      
      // Save to user_profile_summaries collection
      const profileSummaryRef = doc(db, 'user_profile_summaries', selectedUser);
      
      // Extract profile summary specific data
      const profileSummaryData = {
        userID: selectedUser,  // Ensure userID is always set
        profileSummary: profileSummary.profileSummary || '',
        keyHighlights: profileSummary.keyHighlights || {},
        idealRoleCharacteristics: profileSummary.idealRoleCharacteristics || {},
        keySkills: profileSummary.keySkills || [],
        painPoints: profileSummary.painPoints || [],
        potentialFit: profileSummary.potentialFit || '',
        showProfile: profileSummary.showProfile || false,
        updatedAt: serverTimestamp(),
        lastUpdatedBy: user.email
      };
      
      console.log("Saving to user_profile_summaries collection:", profileSummaryData);
      await setDoc(profileSummaryRef, profileSummaryData, { merge: true });
      
      // If this user has waitlist data, also update it there
      if (profileSummary.submissionId) {
        const submissionRef = doc(db, 'waitlist', profileSummary.submissionId);
        
        // Create admin notes
        const adminNotes = {
          notes: profileSummary.notes || '',
          status: profileSummary.status || 'pending',
          rating: profileSummary.rating || '3',
          tags: profileSummary.tags || [],
        };
        
        console.log("Saving to waitlist collection:", profileSummary.submissionId);
        await updateDoc(submissionRef, {
          // Admin notes in a separate field
          adminNotes: adminNotes,
          // Also save profile summary for backward compatibility
          profileSummary: profileSummaryData,
          lastUpdatedByAdmin: {
            timestamp: serverTimestamp(),
            adminEmail: user.email
          }
        });
      }
      
      // Check if user has a user_profile entry
      const userProfileRef = doc(db, 'user_profiles', selectedUser);
      const userProfileSnap = await getDoc(userProfileRef);
      
      if (!userProfileSnap.exists()) {
        // Create a basic user profile if it doesn't exist
        console.log("Creating user_profile document for user:", selectedUser);
        
        const newUserProfile = {
          uid: selectedUser,
          basicInfo: {
            email: profileSummary.email || null,
            firstName: null,
            lastName: null
          },
          createdAt: serverTimestamp(),
          lastUpdated: serverTimestamp(),
          createdBy: "admin",
          isAdmin: false
        };
        
        await setDoc(userProfileRef, newUserProfile);
        setUserProfile(newUserProfile);
      } else {
        // Update the lastUpdated timestamp
        await updateDoc(userProfileRef, {
          lastUpdated: serverTimestamp()
        });
      }
      
      setSaveMessage('Profile data saved successfully!');
    } catch (err) {
      console.error("Error saving profile data:", err);
      setError(`Error: ${err.message}`);
    } finally {
      setIsSaving(false);
    }
  };
  
  const handleLogout = async () => {
    await logout();
    navigate('/');
  };
  
  // Helper function to update nested profile summary property
  const updateProfileSummary = (path, value) => {
    if (!profileSummary) return;
    
    const newProfileSummary = { ...profileSummary };
    const pathParts = path.split('.');
    
    if (pathParts.length === 1) {
      newProfileSummary[path] = value;
    } else {
      let current = newProfileSummary;
      for (let i = 0; i < pathParts.length - 1; i++) {
        const part = pathParts[i];
        
        // Handle array indices
        if (part.includes('[') && part.includes(']')) {
          const arrayName = part.substring(0, part.indexOf('['));
          const index = parseInt(part.substring(part.indexOf('[') + 1, part.indexOf(']')));
          current = current[arrayName][index];
        } else {
          // Create the path if it doesn't exist
          if (!current[part]) {
            current[part] = {};
          }
          current = current[part];
        }
      }
      
      const lastPart = pathParts[pathParts.length - 1];
      // Handle array indices in the last part
      if (lastPart.includes('[') && lastPart.includes(']')) {
        const arrayName = lastPart.substring(0, lastPart.indexOf('['));
        const index = parseInt(lastPart.substring(lastPart.indexOf('[') + 1, lastPart.indexOf(']')));
        current[arrayName][index] = value;
      } else {
        current[lastPart] = value;
      }
    }
    
    setProfileSummary(newProfileSummary);
  };

  // Handle raw input for comma-separated fields
  const handleRawInputChange = (path, value) => {
    setRawInputValues(prev => ({
      ...prev,
      [path]: value
    }));
  };

  // Process comma-separated input on blur
  const handleInputBlur = (path, value) => {
    updateProfileSummary(
      path, 
      value.split(',').map(item => item.trim()).filter(Boolean)
    );
  };

  // When a user is selected, initialize rawInputValues from profileSummary
  useEffect(() => {
    if (profileSummary) {
      const industries = profileSummary.keyHighlights?.industries || [];
      const strengths = profileSummary.keyHighlights?.strengths || [];
      const tags = profileSummary.tags || [];
      const industryFocus = profileSummary.idealRoleCharacteristics?.industryFocus || [];
      const keySkills = (profileSummary.keySkills || []).map(skill => 
        typeof skill === 'string' ? skill : skill.name
      ).join(', ');
      const painPoints = (profileSummary.painPoints || []).join(', ');
      
      setRawInputValues({
        'keyHighlights.industries': industries.join(', '),
        'keyHighlights.strengths': strengths.join(', '),
        'tags': tags.join(', '),
        'idealRoleCharacteristics.industryFocus': Array.isArray(industryFocus) 
          ? industryFocus.map(item => typeof item === 'string' ? item : item.name).join(', ')
          : '',
        'keySkills': keySkills,
        'painPoints': painPoints
      });
    }
  }, [profileSummary]);

  // Process skills from raw input
  const handleSkillsBlur = (value) => {
    // Convert comma-separated string to array of skill objects
    const skills = value.split(',')
      .map(item => item.trim())
      .filter(Boolean)
      .map(name => ({ name, description: '' }));
    
    updateProfileSummary('keySkills', skills);
  };

  if (!user) {
    return (
      <div className="py-24 relative min-h-screen sm:py-32">
        <div className="absolute inset-0 bg-gradient-to-b from-amber-100 via-pink-300 to-purple-500 opacity-10 -z-10"></div>
        <Container>
          <div className="mx-auto max-w-2xl text-center">
            <Logo className="h-12 mx-auto mb-8" />
            <Heading>Admin Access Required</Heading>
            <p className="mt-4 text-lg text-gray-600">You need to be logged in as an admin to view this page.</p>
            <Button as="a" href="/" className="mt-8">Return to Homepage</Button>
          </div>
        </Container>
      </div>
    );
  }

  return (
    <PageLayout showProfileMenu={true} isAdminPage={true}>
      <div>
        <AdminLayout />
      </div>
    </PageLayout>
  );
};

export default AdminDashboard; 